import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { countries, locationType, salutation, userInsuranceType, } from '@tymia/main-api';
import { ensureDateTime, ensureDefined, ifDefined, } from '@tymia/shared';
import { DateTime, } from 'luxon';
import { useCallback, useState, } from 'react';
import { Controller, useForm, } from 'react-hook-form';
import { entries, isEmpty, isIncludedIn, keys, pick, } from 'remeda';
import { api, } from '../../api/Api.js';
import { Button, } from '../../components/Button.js';
import { Checkbox, } from '../../components/Checkbox.js';
import { Select, } from '../../components/Select.js';
import { Textbox, } from '../../components/Textbox.js';
import { VerticalStepper, } from '../../components/VerticalStepper.js';
import { useUpdateEffect, } from '../../hooks/useUpdateEffect.js';
import { EventAvailableIcon, } from '../../icons/EventAvailableIcon.js';
import { useTranslation, } from '../../useTranslation.js';
import { ajvResolver, } from '../../utils/ajvResolver.js';
import { cx, } from '../../utils/ReactUtils.js';
import { WeekCalendar, } from './WeekCalendar.js';
function BookingComponentInner(props) {
    const tRoot = useTranslation();
    const t = tRoot.appointment.bookingWidget;
    function initialStartDate() {
        return ensureDateTime(ifDefined(props.appointments[0]?.appointmentStart, DateTime.fromISO) ?? DateTime.now())
            .setLocale(tRoot.locale)
            .startOf('week', { useLocaleWeeks: true })
            .toISO();
    }
    const [apiRequest, setApiRequest] = useState(() => ({
        therapistId: props.therapistId,
        startDate: initialStartDate(),
        endDate: null,
        insuranceType: null,
        typeId: null,
    }));
    const [apiResponse, setApiResponse] = useState(props);
    const [askLocation, setAskLocation] = useState(false);
    useUpdateEffect(() => {
        let isMounted = true;
        void (async () => {
            const response = await api.client.appointment.bookable.request({
                therapistId: apiRequest.therapistId,
                startDate: apiRequest.startDate,
                endDate: apiRequest.endDate,
                insuranceType: apiRequest.insuranceType ?? undefined,
                meetingTypeId: apiRequest.typeId ?? undefined,
            });
            if (response.status === 'ok' && isMounted) {
                setApiResponse(r => ({
                    ...r,
                    ...response,
                }));
            }
        })();
        return () => {
            isMounted = false;
        };
    }, Object.values(apiRequest));
    const [step, setStep] = useState('select appointment');
    const { control, getValues, getFieldState, setValue, trigger, register, formState: { errors, }, } = useForm({
        defaultValues: {
            ...apiRequest,
            appointment: undefined,
            location: null,
            salutation: null,
            firstName: '',
            lastName: '',
            email: '',
            phoneCountry: 'DE',
            phone: '',
            street: '',
            postalCode: '',
            city: '',
            privacyConsent: false,
        },
        resolver: ajvResolver(api.client.appointment.book.validateRequest, {
            locale: tRoot.locale,
        }),
    });
    const setFormValues = useCallback((values) => {
        if (values.typeId) {
            const l = apiResponse.types.find(({ id }) => id === values.typeId)?.location;
            // set or reset location based on the selected meeting type
            values.location = isIncludedIn(l, locationType) ? l : null;
            setAskLocation(values.location === null);
        }
        setApiRequest(r => {
            const newStateValues = pick(values, keys(r));
            return isEmpty(newStateValues) ? r : {
                ...r,
                ...newStateValues,
            };
        });
        for (const [key, value] of entries(pick(values, keys(getValues())))) {
            const state = getFieldState(key);
            setValue(key, value, {
                // don't bother user with errors while filling out the form
                shouldValidate: state.invalid,
                shouldDirty: true,
            });
        }
    }, [apiResponse, setApiRequest, getValues, setValue]);
    function createTextboxChangeHandler(name) {
        return event => {
            setFormValues({
                [name]: event.currentTarget.value,
            });
        };
    }
    if (step === 'select appointment') {
        const insuranceTypeOptions = userInsuranceType
            .map(type => ({
            value: type,
            label: t.insuranceTypeOption(type),
            disabled: apiResponse.types.some(({ insuranceType }) => insuranceType === type || !insuranceType) === false,
        }));
        function getMeetingOptions(currentApiRequest = apiRequest) {
            const currentInsuranceType = currentApiRequest.insuranceType;
            return apiResponse.types
                .filter(type => 
            // nothing is selected, so allow all values
            !currentInsuranceType
                // the appointment type has no insurance restriction
                || !type.insuranceType
                || type.insuranceType === currentInsuranceType);
        }
        const meetingTypeOptions = getMeetingOptions();
        // auto-select if only one option is available
        if (apiResponse.typesFiltered.length === 1
            && !apiRequest.typeId
            && meetingTypeOptions.some(mt => mt.id === apiResponse.typesFiltered[0])) {
            setFormValues({
                typeId: apiResponse.typesFiltered[0],
            });
        }
        // if user changed the insurance type, then
        // the selected meeting type might not be
        // valid anymore, so reset it
        if (apiRequest.typeId && meetingTypeOptions.some(mt => mt.id === apiRequest.typeId) === false) {
            setFormValues({
                typeId: null,
            });
        }
        const events = apiResponse.appointments.map(a => ({
            id: a.id,
            at: ensureDateTime(DateTime.fromISO(a.appointmentStart)).setLocale(tRoot.locale),
        }));
        const start = ensureDateTime(DateTime.fromISO(apiResponse.appointments[0]?.appointmentStart ?? apiRequest.startDate))
            .setLocale(tRoot.locale)
            .startOf('week', { useLocaleWeeks: true });
        const end = start.endOf('week', { useLocaleWeeks: true });
        const selectDate = (event) => {
            setFormValues({ appointment: event });
            setStep('enter personal data');
        };
        const meetingTypeDisabled = !apiRequest.insuranceType;
        const calendarDisabled = meetingTypeDisabled || !apiRequest.typeId;
        return (_jsxs("div", { className: "max-w-[450px] flex-1 min-h-0 overflow-auto", children: [_jsx(VerticalStepper, { steps: [
                        {
                            title: _jsx("span", { className: "font-medium", children: t.SelectInsuranceTypeLabel }),
                            content: (_jsx(Controller, { name: "insuranceType", control: control, render: ({ field }) => (_jsx(Select, { label: field.value
                                        ? t.SelectInsuranceTypeSelectSelected
                                        : t.SelectInsuranceTypeSelectEmpty, options: insuranceTypeOptions, value: [field.value], onSelect: selectedInsuranceType => {
                                        setFormValues({
                                            insuranceType: selectedInsuranceType[0] ?? null,
                                            startDate: initialStartDate(),
                                            endDate: null,
                                        });
                                    }, position: "external" })) })),
                        },
                        {
                            title: _jsx("span", { className: "font-medium", children: t.SelectMeetingTypeLabel }),
                            content: (_jsx(Controller, { name: "typeId", control: control, render: ({ field }) => (_jsx(Select, { disabled: meetingTypeDisabled, label: field.value ? t.SelectMeetingTypeSelectSelected : t.SelectMeetingTypeSelectEmpty, options: meetingTypeOptions.map(type => ({
                                        value: type.id,
                                        label: t.meetingTypeOption(type),
                                    })), value: [field.value], onSelect: selectedMeetingType => {
                                        setFormValues({
                                            typeId: selectedMeetingType[0] ?? null,
                                            startDate: initialStartDate(),
                                            endDate: null,
                                        });
                                    }, position: "external" })) })),
                        },
                        {
                            title: _jsx("span", { className: "font-medium", children: t.SelectAppointment }),
                        },
                    ] }), _jsxs("div", { className: cx(
                    // hieararchy between cursor-not-allowed
                    // and pointer-events-none is important,
                    // because the second disables hover
                    {
                        'cursor-not-allowed': calendarDisabled,
                    }), children: [_jsxs("div", { className: cx('flex mx-auto flex-1 min-h-0', {
                                'pointer-events-none opacity-50': calendarDisabled,
                            }), children: [_jsx("div", { className: cx('px-2 py-0.5 m-1 rounded-md border border-tym-primary text-tym-primary hover:bg-gray-100', apiResponse.prevCursor ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'), onClick: () => apiResponse.prevCursor && setFormValues({
                                        startDate: start.minus({ weeks: 1 }).toISO(),
                                        endDate: end.minus({ weeks: 1 }).toISO(),
                                    }), children: "\u140A" }), _jsx("div", { className: "flex-1 flex items-center justify-center", children: _jsx("div", { className: "text-sm text-tym-gray", children: tRoot.common.formatWeekRange(start, end) }) }), _jsx("div", { className: cx('px-2 py-0.5 m-1 rounded-md border border-tym-primary text-tym-primary hover:bg-gray-100', apiResponse.nextCursor ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'), onClick: () => apiResponse.nextCursor && setFormValues({
                                        startDate: start.plus({ weeks: 1 }).toISO(),
                                        endDate: end.plus({ weeks: 1 }).toISO(),
                                    }), children: "\u1405" })] }), _jsx("div", { className: "flex mx-auto flex-1 min-h-0", children: _jsx(WeekCalendar, { anchorDate: start, events: events, onClick: selectDate, disabled: calendarDisabled }) })] })] }, step));
    }
    if (step === 'enter personal data') {
        return (_jsx("div", { className: "max-w-[450px] flex-1 min-h-0 overflow-auto", children: _jsx(VerticalStepper, { steps: [
                    {
                        title: _jsx("span", { className: "font-medium", children: t.PersonalData }),
                        content: (_jsxs("div", { children: [_jsx(Controller, { name: "salutation", control: control, render: ({ field, fieldState }) => (_jsx(Select, { label: tRoot.user.Salutation, options: salutation.map(type => ({
                                            value: type,
                                            label: type,
                                        })), value: [field.value], onSelect: newSalutation => {
                                            setFormValues({ salutation: newSalutation[0] ?? null });
                                        }, position: "external", error: fieldState.error?.message })) }), _jsx(Textbox, { ...register('firstName', {
                                        onChange: createTextboxChangeHandler('firstName'),
                                    }), label: tRoot.user.FirstName, position: "external", autoComplete: "given-name", error: errors.firstName?.message }), _jsx(Textbox, { ...register('lastName', {
                                        onChange: createTextboxChangeHandler('lastName'),
                                    }), label: tRoot.user.LastName, position: "external", autoComplete: "family-name", error: errors.lastName?.message }), _jsx(Textbox, { ...register('email', {
                                        onChange: createTextboxChangeHandler('email'),
                                    }), label: tRoot.user.EMail, position: "external", inputMode: "email", autoComplete: "email", type: "email", error: errors.email?.message }), _jsxs("div", { className: "grid grid-cols-12", children: [_jsx("div", { className: "col-span-4", children: _jsx(Controller, { name: "phoneCountry", control: control, render: ({ field, fieldState }) => (_jsx(Select, { label: tRoot.user.PhoneCountry, options: countries.map(country => ({
                                                        value: country.iso,
                                                        label: `${country[tRoot.locale]} (${country.phoneCode})`,
                                                        displayLabel: country.phoneCode,
                                                    })), value: [field.value], onSelect: country => {
                                                        setFormValues({ phoneCountry: ensureDefined(country[0]) });
                                                    }, position: "external-left", error: fieldState.error?.message })) }) }), _jsx("div", { className: "col-span-8", children: _jsx(Textbox, { ...register('phone', {
                                                    onChange: createTextboxChangeHandler('phone'),
                                                }), label: tRoot.user.PhoneMobile, position: "external-right", inputMode: "tel", autoComplete: "tel", type: "tel", error: errors.phone?.message }) })] }), _jsx(Button, { position: "external", className: "text-center", size: "lg", onClick: async () => {
                                        if (await trigger([
                                            'salutation',
                                            'firstName',
                                            'lastName',
                                            'email',
                                            'phoneCountry',
                                            'phone',
                                        ])) {
                                            setStep('enter address');
                                        }
                                    }, children: tRoot.common.button.next })] })),
                    },
                ] }) }, step));
    }
    if (step === 'enter address') {
        return (_jsx("div", { className: "max-w-[450px] flex-1 min-h-0 overflow-auto", children: _jsx(VerticalStepper, { steps: [
                    {
                        title: _jsx("span", { className: "font-medium", children: t.PersonalData }),
                        content: (_jsxs("div", { children: [_jsx(Textbox, { ...register('street', {
                                        onChange: createTextboxChangeHandler('street'),
                                    }), label: tRoot.user.Street, position: "external", autoComplete: "street-address", error: errors.street?.message }), _jsx(Textbox, { ...register('postalCode', {
                                        onChange: createTextboxChangeHandler('postalCode'),
                                    }), label: tRoot.user.PostalCode, position: "external", inputMode: "numeric", autoComplete: "postal-code", error: errors.postalCode?.message }), _jsx(Textbox, { ...register('city', {
                                        onChange: createTextboxChangeHandler('city'),
                                    }), label: tRoot.user.City, position: "external", autoComplete: "address-level2", error: errors.city?.message }), askLocation && (_jsx(Controller, { name: "location", control: control, render: ({ field, fieldState }) => (_jsx(Select, { label: t.SelectLocationLabel, options: locationType.map(type => ({
                                            value: type,
                                            label: tRoot.user.searchTherapist.filter.location.options[type],
                                        })), value: field.value ? [field.value] : [], onSelect: newLocation => setFormValues({ location: ensureDefined(newLocation[0]) }), position: "external", error: fieldState.error?.message })) }))] })),
                    },
                    {
                        title: (_jsx("a", { href: "https://www.tymia.de/datenschutz", target: "_blank", className: "font-medium underline text-black", children: t.PrivacyConsent })),
                        content: (_jsxs("div", { children: [_jsx("div", { className: "text-sm", children: t.PrivacyConsentText }), _jsx(Controller, { name: "privacyConsent", control: control, render: ({ field, fieldState }) => (_jsx(Checkbox, { label: t.PrivacyConsentLabel, value: field.value, onChange: value => setFormValues({ privacyConsent: value }), error: !!fieldState.error?.message, position: "external", className: "text-sm" })) }), _jsx(Button, { position: "external", className: "text-center", size: "lg", onClick: async () => {
                                        if (await trigger([
                                            'street',
                                            'postalCode',
                                            'city',
                                            'location',
                                            'privacyConsent',
                                        ])) {
                                            const values = getValues();
                                            const params = {
                                                locale: tRoot.locale,
                                                ...pick(values, [
                                                    'firstName',
                                                    'lastName',
                                                    'email',
                                                    'phoneCountry',
                                                    'phone',
                                                    'street',
                                                    'postalCode',
                                                    'city',
                                                ]),
                                                appointmentId: ensureDefined(values.appointment?.id),
                                                typeId: ensureDefined(values.typeId),
                                                insuranceType: ensureDefined(values.insuranceType),
                                                location: ensureDefined(values.location),
                                                salutation: ensureDefined(values.salutation),
                                                privacyConsent: values.privacyConsent,
                                            };
                                            const response = await api.client.appointment.book.request(params);
                                            if (response.status === 'ok') {
                                                setStep('booked');
                                            }
                                        }
                                    }, children: t.BookAppointment })] })),
                    },
                ] }) }, step));
    }
    if (step === 'booked') {
        const selectedAppointmentType = ensureDefined(apiResponse.types.find(({ id }) => id === getValues().typeId));
        return (_jsxs("div", { className: "max-w-[450px] flex-1 min-h-0 overflow-auto", children: [_jsxs("h2", { children: [_jsx(EventAvailableIcon, { className: "inline fill-tym-blue" }), " ", t.BookingSuccessHeader] }), _jsx("p", { children: t.BookingSuccessMessage }), _jsx("p", { className: "component-position-external", children: t.meetingTypeOption(selectedAppointmentType) }), _jsx("p", { className: "component-position-external", children: t.bookingSuccessDetails({
                        appointmentStart: ensureDefined(getValues().appointment?.at),
                    }) })] }));
    }
    return _jsx("div", { children: step });
}
export function BookingComponent(props) {
    const tRoot = useTranslation();
    const { noAvailableAppointments } = tRoot.user.searchTherapist;
    if (props.appointments.length === 0) {
        return _jsx("div", { children: noAvailableAppointments('available') });
    }
    return _jsx(BookingComponentInner, { ...props });
}
//# sourceMappingURL=BookingComponent.js.map